import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Clock,
  Plus,
  Search,
  AlertCircle,
  CheckCircle,
  Users,
  ChevronRight,
} from 'lucide-react'
import { shiftService } from '../../services/shiftService'
import { ShiftModel } from '../../types'

export function ShiftsPage() {
  const [shifts, setShifts] = useState<ShiftModel[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [filterType, setFilterType] = useState<'5R' | '3R' | ''>('')

  useEffect(() => {
    loadShifts()
  }, [])

  const loadShifts = async () => {
    try {
      setLoading(true)
      const data = await shiftService.getAll(true)
      setShifts(data)
    } catch (error) {
      toast.error('Error al cargar los turnos')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Filter shifts
  const filteredShifts = shifts.filter((shiftItem) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      if (
        !shiftItem.code.toLowerCase().includes(search) &&
        !shiftItem.name.toLowerCase().includes(search) &&
        !(shiftItem.description?.toLowerCase().includes(search))
      ) {
        return false
      }
    }
    if (filterActive !== '' && shiftItem.isActive !== filterActive) return false
    if (filterType !== '' && shiftItem.type !== filterType) return false
    return true
  })

  // Separate shifts by type for display
  const shifts5R = filteredShifts.filter(s => s.type === '5R')
  const shifts3R = filteredShifts.filter(s => s.type === '3R')

  // Get badge color based on shift code
  const getBadgeColor = (code: string) => {
    const colorMap: Record<string, string> = {
      A: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      B: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      C: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      D: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      E: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      R3: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    }
    return colorMap[code] || 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  }

  const ShiftCard = ({ shiftItem }: { shiftItem: ShiftModel }) => (
    <Link
      to={`/configuration/shifts/${shiftItem.id}`}
      className={`card hover:shadow-lg transition-all cursor-pointer group ${!shiftItem.isActive ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${getBadgeColor(shiftItem.code)}`}>
              {shiftItem.code}
            </span>
            <span className={`badge text-xs ${shiftItem.type === '5R' ? 'badge-primary' : 'badge-warning'}`}>
              {shiftItem.type}
            </span>
          </div>
          <span
            className={`badge text-xs ${shiftItem.isActive ? 'badge-success' : 'badge-gray'}`}
          >
            {shiftItem.isActive ? 'Activo' : 'Inactivo'}
          </span>
        </div>

        <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-500 transition-colors" />
      </div>

      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
        {shiftItem.name}
      </h3>
      {shiftItem.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
          {shiftItem.description}
        </p>
      )}

      {shiftItem.employeeCount !== undefined && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Users className="h-4 w-4" />
            <span>{shiftItem.employeeCount} empleados asignados</span>
          </div>
        </div>
      )}
    </Link>
  )

  return (
    <div className="animate-slide-in">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Clock className="h-6 w-6" />
              <span className="hidden sm:inline">Turnos de Trabajo</span>
              <span className="sm:hidden">Turnos</span>
            </h1>
            <p className="page-subtitle">
              Gestiona los turnos de trabajo de la empresa
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/configuration/shifts/new"
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuevo Turno</span>
              <span className="sm:hidden">Nuevo</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por codigo, nombre o descripcion..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as '5R' | '3R' | '')}
            className="input sm:w-40"
          >
            <option value="">Todos los tipos</option>
            <option value="5R">5 Relevos</option>
            <option value="3R">3 Relevos</option>
          </select>

          <select
            value={filterActive === '' ? '' : filterActive ? 'true' : 'false'}
            onChange={(e) =>
              setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
            }
            className="input sm:w-40"
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Shifts List */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </div>
      ) : filteredShifts.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No hay turnos configurados
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Crea un nuevo turno para comenzar
            </p>
            <Link to="/configuration/shifts/new" className="btn btn-primary">
              Crear primer turno
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* 5R Shifts Section */}
          {shifts5R.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Sistema de 5 Relevos (5R)
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Utilizado en: Nave 1, Nave 2, Nave 3, Nave 4, Impresora, Calidad, Laboratorio, Nanopack y Otros
              </p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {shifts5R.map((shiftItem) => (
                  <ShiftCard key={shiftItem.id} shiftItem={shiftItem} />
                ))}
              </div>
            </div>
          )}

          {/* 3R Shifts Section */}
          {shifts3R.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Sistema de 3 Relevos (3R)
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Utilizado en: Centro de Plisado
              </p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {shifts3R.map((shiftItem) => (
                  <ShiftCard key={shiftItem.id} shiftItem={shiftItem} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Stats summary */}
      {!loading && shifts.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Clock className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {shifts.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total de turnos
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-success-100 dark:bg-success-900/30">
              <CheckCircle className="h-6 w-6 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {shifts.filter((s) => s.isActive).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Turnos activos
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {shifts.reduce((sum, s) => sum + (s.employeeCount || 0), 0)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Empleados totales
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30">
              <CheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {shifts.filter(s => s.type === '5R').length} / {shifts.filter(s => s.type === '3R').length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                5R / 3R
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
